/**
 * LocalPPlansPage — Plan (lịch giao dự kiến) cho Local Purchase. Đồng bộ ContractListView.
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Divider, Popover, Stack, Table, TableBody, TableCell,
  TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useDeleteAllPlansMutation, useDeletePlanMutation, useListPlansQuery,
} from '@/store/api/localPPlansApi'
import type { LocalPPlanOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import PlanFormDialog from './PlanFormDialog'

type FilterKind = 'search' | 'contractNo'
const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, textTransform: 'none', fontSize: '0.7rem', lineHeight: 1.1,
  '&.MuiButton-outlined': { borderColor: alpha(theme.palette.text.primary, 0.14), '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.24), backgroundColor: alpha(theme.palette.text.primary, 0.04) } },
})
const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = { key: keyof LocalPPlanOut; label: string; kind?: ColumnKind; align?: 'right'; digits?: number }

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no', label: 'Số HĐ' },
  { key: 'supplier_name', label: 'Nhà cung cấp' },
  { key: 'plan_date', label: 'Ngày Plan', kind: 'date' },
  { key: 'plan_quantity', label: 'SL Plan (kg)', kind: 'number', align: 'right', digits: 0 },
  { key: 'note', label: 'Ghi chú' },
  { key: 'created_date', label: 'Tạo lúc', kind: 'datetime' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}` }
const formatAccessDateTime = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${formatAccessDate(v)} ${`${d.getHours()}`.padStart(2, '0')}:${`${d.getMinutes()}`.padStart(2, '0')}` }
const formatNumberFixed = (v: number, digits = 0): string => digits > 0 ? v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits }) : (Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))

const rowMatchesSearch = (row: LocalPPlanOut, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  const fields: Array<keyof LocalPPlanOut> = ['contract_no', 'supplier_name', 'note']
  return fields.some((k) => { const v = row[k]; return v != null && String(v).toLowerCase().includes(needle) })
}

const cellValue = (row: LocalPPlanOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'datetime') return formatAccessDateTime(val)
  if (col.kind === 'number' && typeof val === 'number') return formatNumberFixed(val, col.digits ?? 0)
  if (typeof val === 'number') return formatNumberFixed(val, 0)
  return String(val)
}

export function LocalPPlansPage() {
  const { canEdit } = usePagePermission()
  const navigate = useNavigate()
  const snack = useSnackbar()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [contractNoFilter, setContractNoFilter] = useState('')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || contractNoFilter)
  const clearAllFilters = () => { setSearchTerm(''); setContractNoFilter(''); setPage(0) }

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<LocalPPlanOut | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<LocalPPlanOut | null>(null)
  const [deleteAllOpen, setDeleteAllOpen] = useState(false)
  const [deleteAllConfirm, setDeleteAllConfirm] = useState(false)

  const { data, isLoading, isFetching, error } = useListPlansQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const [deletePlan, { isLoading: deleting }] = useDeletePlanMutation()
  const [deleteAllPlans, { isLoading: deletingAll }] = useDeleteAllPlansMutation()

  const baseRows = useMemo(() => data?.items ?? [], [data])
  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row, searchTerm)) return false
      if (contractNoFilter && row.contract_no !== contractNoFilter) return false
      return true
    }), [baseRows, searchTerm, contractNoFilter])

  const rows = useMemo(() => rowsPerPage === -1 ? filteredRows : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredRows, page, rowsPerPage])

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.id === selectedId) ?? null, [baseRows, selectedId])

  const handleViewContract = () => { if (!selectedRow) return; navigate(`/purchase/local-p/${encodeURIComponent(selectedRow.contract_no)}`) }
  const handleEdit = () => { if (selectedRow) setEditing(selectedRow) }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try { await deletePlan(confirmDelete.id).unwrap(); snack.success('Đã xoá plan'); setConfirmDelete(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Xoá plan thất bại')) }
  }
  const handleDeleteAll = async () => {
    if (!contractNoFilter) return
    try { await deleteAllPlans(contractNoFilter).unwrap(); snack.success(`Đã xoá tất cả plan của ${contractNoFilter}`); setDeleteAllOpen(false); setDeleteAllConfirm(false) }
    catch (err) { snack.error(getErrorMessage(err, 'Xoá toàn bộ plan thất bại')) }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          <Button size="small" variant={contractNoFilter ? 'contained' : 'outlined'} color={contractNoFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('contractNo', e)} sx={filterBarBtnSx}>Contract No</Button>
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New Plan</Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleEdit}>Edit</Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => selectedRow && setConfirmDelete(selectedRow)}>Delete</Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!contractNoFilter || !canEdit} onClick={() => setDeleteAllOpen(true)}>Delete All for HĐ</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={handleViewContract}>View HĐ</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load plans.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }} />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="Số HĐ, supplier, ghi chú..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'contractNo' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Số HĐ" placeholder="P-XXXXX" value={contractNoFilter} onChange={(e) => { setContractNoFilter(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setContractNoFilter(''); setPage(0); closeFilterPopover() }}>Clear</Button>
            </Stack>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_: number, row: LocalPPlanOut) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 1300, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) setSelectedId(id) }} />
              },
            }}
          />
        </Box>
      </Box>

      <PlanFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <PlanFormDialog open={!!editing} mode="edit" initialValue={editing} onClose={() => setEditing(null)} />
      <ConfirmDialog open={!!confirmDelete} title="Xoá plan" message={confirmDelete ? `Xoá plan ngày ${formatAccessDate(confirmDelete.plan_date)} của ${confirmDelete.contract_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDelete(null)} />
      <ConfirmDialog open={deleteAllOpen && !deleteAllConfirm} title="Xoá tất cả Plan" message={`Xoá toàn bộ Plan của hợp đồng ${contractNoFilter}? Bấm tiếp để xác nhận.`} confirmText="Tiếp" destructive onConfirm={() => setDeleteAllConfirm(true)} onCancel={() => { setDeleteAllOpen(false); setDeleteAllConfirm(false) }} />
      <ConfirmDialog open={deleteAllOpen && deleteAllConfirm} title="Xác nhận lần cuối" message={`Bạn CHẮC CHẮN muốn xoá TẤT CẢ Plan của hợp đồng ${contractNoFilter}? Thao tác này không thể khôi phục.`} destructive loading={deletingAll} onConfirm={() => void handleDeleteAll()} onCancel={() => { setDeleteAllOpen(false); setDeleteAllConfirm(false) }} />
    </Box>
  )
}

export default LocalPPlansPage
